import {
  Button,
  ChevronLeft,
  ChevronRight,
} from "@ews-admin/global-design-system";
import { Select } from "../../atoms/Select";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

import { scrollToTop } from "../../../utils/scroll-utils";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showPageNumbers?: boolean;
  maxVisiblePages?: number;
  showItemsPerPageSelector?: boolean;
  itemsPerPageOptions?: number[];
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
  showItemsPerPageSelector = true,
  itemsPerPageOptions = [5, 10, 25, 50, 100],
  className = "",
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Navigate, then bring the page back to its top
  const goToPage = (page: number) => {
    onPageChange(page);
    scrollToTop(containerRef.current);
  };

  // Calculate visible page range
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of middle range
      let start = Math.max(2, currentPage - halfVisible);
      let end = Math.min(totalPages - 1, currentPage + halfVisible);

      // Adjust if we're near the beginning
      if (currentPage <= halfVisible) {
        end = Math.min(totalPages - 1, maxVisiblePages - 1);
      }

      // Adjust if we're near the end
      if (currentPage > totalPages - halfVisible) {
        start = Math.max(2, totalPages - maxVisiblePages + 2);
      }

      // Add ellipsis if there's a gap
      if (start > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if there's a gap
      if (end < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page if there's more than one page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === "number") {
      goToPage(page);
    }
  };

  if (totalPages <= 1) {
    return null; // Don't show pagination if there's only one page or no pages
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      {/* Single line layout - responsive */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
        {/* Left side - Items info */}
        <div className="order-2 text-sm text-neutral-600 lg:order-1">
          {t("pagination.showing", {
            start: startItem,
            end: endItem,
            total: totalItems,
          })}
        </div>

        {/* Center - Pagination controls */}
        <div className="flex order-1 justify-center items-center space-x-1 lg:order-2">
          {/* Previous button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="flex items-center space-x-1"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t("pagination.previous")}</span>
          </Button>

          {/* Page numbers */}
          {showPageNumbers && (
            <div className="flex items-center space-x-1">
              {visiblePages.map((page, index) => (
                <React.Fragment key={index}>
                  {typeof page === "number" ? (
                    <Button
                      variant={currentPage === page ? "ews-primary" : "outline"}
                      size="sm"
                      onClick={() => handlePageClick(page)}
                      className="min-w-[40px]"
                    >
                      {page}
                    </Button>
                  ) : (
                    <span className="px-2 py-1 text-neutral-500">...</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Next button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="flex items-center space-x-1"
          >
            <span className="hidden sm:inline">{t("pagination.next")}</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Right side - Items per page selector */}
        {showItemsPerPageSelector && onItemsPerPageChange && (
          <div className="flex order-3 justify-center items-center space-x-2 lg:order-3">
            <div className="min-w-[100px]">
              <Select
                label={t("pagination.itemsPerPage")}
                value={itemsPerPage.toString()}
                onChange={(value: unknown) =>
                  onItemsPerPageChange(parseInt(value as string))
                }
                options={itemsPerPageOptions.map(option => ({
                  label: option.toString(),
                  value: option.toString(),
                }))}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
